import { Link } from 'react-router-dom';

import { En } from '../i18n/En';
import { formatDate } from '../i18n/format';
import { t } from '../i18n/t';

export type RetellingRow = {
  readonly id: string;
  readonly firstPage: number;
  readonly lastPage: number;
  readonly createdAt: string;
  readonly status: string;
  readonly book: { readonly id: string; readonly title: string; readonly cover: string } | null | undefined;
};

/**
 * An adult's retellings (listen & write design §7): book, pages, date, and a
 * way back to the feedback. No scores and no counts. A retelling whose book
 * is no longer on the shelf has nowhere to open, so it is not listed.
 */
export function RetellingList({ retellings }: { retellings: ReadonlyArray<RetellingRow> }) {
  const rows = retellings.flatMap((r) => (r.book ? [{ ...r, book: r.book }] : []));
  return (
    <>
      <h3 className="mt-4 font-semibold">{t('history-listen-title')}</h3>
      {rows.length === 0 ? (
        <p className="text-base-content/60 text-sm">{t('history-listen-none')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/books/${r.book.id}/listen/${r.firstPage}?retelling=${encodeURIComponent(r.id)}`}
                className="hover:bg-base-200 rounded-box flex min-h-(--hit-min) items-center gap-3 p-1"
              >
                <span aria-hidden className="text-2xl">
                  {r.book.cover}
                </span>
                <span className="flex flex-1 flex-col">
                  <span className="font-medium">
                    <En>{r.book.title}</En>
                  </span>
                  <span className="text-base-content/60 text-sm">
                    {r.firstPage === r.lastPage
                      ? t('history-listen-page', { first: r.firstPage })
                      : t('history-listen-pages', { first: r.firstPage, last: r.lastPage })}
                    {r.status === 'PENDING' && ` · ${t('history-listen-pending')}`}
                    {r.status === 'FAILED' && ` · ${t('history-listen-failed')}`}
                  </span>
                </span>
                <span className="text-base-content/60 text-sm">{formatDate(new Date(r.createdAt))}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
